import { Modal } from 'bootstrap';

// bulk_actions.js — checkbox-driven bulk operations on the applicants index.
//
// Wires:
//   - #select-all-applicants  toggles every .bulk-row-check
//   - #bulk-action-bar        show/hide + count based on selection size
//   - buttons with [data-bulk-submit="ACTION"] submit the outer form with
//     hidden `batch_action=ACTION`
//   - [data-bulk-reject-submit] populates the outer form with reason + note
//     from the bulk-reject modal, then submits with batch_action=reject
//   - [data-bulk-clear]        unchecks everything
//
// Keyboard shortcuts (when focus isn't in an input):
//   j / ↓   next row
//   k / ↑   prev row
//   x       toggle highlighted row's checkbox
//   r       open reject modal
//   Esc     clear selection
//
// The outer form has id="applicants-bulk-form" and submits to
// applicants#batch_update. The controller reads batch_ids[] and batch_action.

const FORM_ID = 'applicants-bulk-form';
const ROW_CHECK_CLASS = 'bulk-row-check';
const SELECTED_ROW_CLASS = 'table-active';
const HIGHLIGHTED_ROW_CLASS = 'table-primary';

function form() {
  return document.getElementById(FORM_ID);
}

function checkboxes() {
  return Array.from(document.querySelectorAll(`.${ROW_CHECK_CLASS}`));
}

function checkedCount() {
  return checkboxes().filter(cb => cb.checked).length;
}

function updateBar() {
  const bar   = document.getElementById('bulk-action-bar');
  const count = document.getElementById('bulk-selected-count');
  if (!bar || !count) return;
  const n = checkedCount();
  if (n === 0) {
    bar.classList.add('d-none');
  } else {
    bar.classList.remove('d-none');
    const tpl = count.getAttribute('data-template') || '__N__ selected';
    count.textContent = tpl.replace('__N__', n);
  }
}

function submitWithAction(action) {
  const f = form();
  if (!f) return;
  const hidden = f.querySelector('input[name="batch_action"]');
  if (hidden) hidden.value = action;
  f.submit();
}

function highlight(row) {
  document.querySelectorAll(`.${HIGHLIGHTED_ROW_CLASS}`).forEach(r =>
    r.classList.remove(HIGHLIGHTED_ROW_CLASS));
  if (row) row.classList.add(HIGHLIGHTED_ROW_CLASS);
}

function currentHighlight() {
  return document.querySelector(`.${HIGHLIGHTED_ROW_CLASS}`);
}

function allRows() {
  return Array.from(document.querySelectorAll('[data-applicant-row]'));
}

function moveHighlight(delta) {
  const rows = allRows();
  if (!rows.length) return;
  const cur = currentHighlight();
  const idx = cur ? rows.indexOf(cur) : -1;
  const next = rows[Math.max(0, Math.min(rows.length - 1, idx + delta))];
  highlight(next);
  if (next && next.scrollIntoView) next.scrollIntoView({ block: 'nearest' });
}

function toggleHighlightedCheckbox() {
  const row = currentHighlight();
  if (!row) return;
  const cb = row.querySelector(`.${ROW_CHECK_CLASS}`);
  if (cb) {
    cb.checked = !cb.checked;
    onRowCheckChange();
  }
}

function onRowCheckChange() {
  // Sync selected-row visual state + top bar count.
  checkboxes().forEach(cb => {
    const row = cb.closest('tr');
    if (!row) return;
    row.classList.toggle(SELECTED_ROW_CLASS, cb.checked);
  });
  updateBar();
}

function onSelectAllChange(e) {
  const checked = e.target.checked;
  checkboxes().forEach(cb => { cb.checked = checked; });
  onRowCheckChange();
}

function bindBulkRejectSubmit() {
  const btn = document.querySelector('[data-bulk-reject-submit]');
  if (!btn) return;
  btn.addEventListener('click', () => {
    const reason = document.getElementById('bulk-reject-reason');
    const note = document.getElementById('bulk-reject-note');
    if (!reason || !reason.value) {
      reason && reason.focus();
      return;
    }
    const f = form();
    if (!f) return;
    // Append reason + note into the outer form so they land as params.
    ensureHiddenField(f, 'rejection_reason_id', reason.value);
    ensureHiddenField(f, 'rejection_note', note ? note.value : '');
    submitWithAction('reject');
  });
}

function ensureHiddenField(form, name, value) {
  let el = form.querySelector(`input[type=hidden][name="${name}"]`);
  if (!el) {
    el = document.createElement('input');
    el.type = 'hidden';
    el.name = name;
    form.appendChild(el);
  }
  el.value = value;
}

function bindKeyboard() {
  document.addEventListener('keydown', (e) => {
    // Don't fire when typing.
    const t = e.target;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    // Only when the applicants form is present on this page.
    if (!document.getElementById(FORM_ID)) return;

    if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1); }
    else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1); }
    else if (e.key === 'x') { e.preventDefault(); toggleHighlightedCheckbox(); }
    else if (e.key === 'r') {
      if (checkedCount() === 0) toggleHighlightedCheckbox();
      if (checkedCount() > 0) {
        e.preventDefault();
        const modalEl = document.getElementById('bulk-reject-modal');
        if (modalEl) new Modal(modalEl).show();
      }
    }
    else if (e.key === 'Escape') {
      checkboxes().forEach(cb => { cb.checked = false; });
      onRowCheckChange();
      highlight(null);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const f = form();
  if (!f) return;

  const selectAll = document.getElementById('select-all-applicants');
  if (selectAll) selectAll.addEventListener('change', onSelectAllChange);

  document.querySelectorAll(`.${ROW_CHECK_CLASS}`).forEach(cb =>
    cb.addEventListener('change', onRowCheckChange));

  document.querySelectorAll('[data-bulk-submit]').forEach(btn =>
    btn.addEventListener('click', () => submitWithAction(btn.getAttribute('data-bulk-submit'))));

  const clearBtn = document.querySelector('[data-bulk-clear]');
  if (clearBtn) clearBtn.addEventListener('click', () => {
    checkboxes().forEach(cb => { cb.checked = false; });
    onRowCheckChange();
  });

  bindBulkRejectSubmit();
  bindKeyboard();
  updateBar();
});
